// packages/api/src/hooks/admin/useAnalysisQueue.ts
//
// Phase 12 analista-queue-kpis-notif Plan 11 (REQ-10).
// GET /admin/proposals/analysis-queue — fila de propostas em análise.
//
// Contract: docs/contracts/ESCR-analista-queue-kpis-notif-05.md +
// ESCR-analista-queue-kpis-notif-02.md (backend Plan 07).
//
// Backend: 4 segmentos (mine/unclaimed/all/sla_critical) × 3 sorts × 3 views;
// retorna {count, rows} (Pagination Standard).

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import type {
  AnalysisQueueFilters,
  AnalysisQueueKanbanColumn,
  AnalysisQueueRow,
  ApiSuccess,
} from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

interface AnalysisQueueBackendResponse {
  count: number;
  rows: AnalysisQueueRow[];
}

export interface UseAnalysisQueueResult {
  count: number;
  rows: AnalysisQueueRow[];
}

export interface UseAnalysisQueueOptions {
  enabled?: boolean;
}

export type AnalysisQueueInfiniteFilters = Omit<
  AnalysisQueueFilters,
  'page' | 'limit' | 'pageSize' | 'view' | 'kanbanColumn'
> & {
  kanbanColumn: AnalysisQueueKanbanColumn;
};

export interface AnalysisQueueInfinitePage extends UseAnalysisQueueResult {}

const ANALYSIS_KANBAN_PAGE_SIZE = 10;

/**
 * Lista paginada da fila de análise.
 *
 * Permission: `proposals:analyze` (analista + gerente + admin).
 *
 * **staleTime 30s**: fila precisa refresh frequente (SLAs decaindo, claims/
 * releases concorrentes). Plan 12 acopla Socket.IO invalidate em eventos
 * `proposal.assigned` / `proposal.released` / `proposal.status_changed`.
 */
export function useAnalysisQueue(
  filters: AnalysisQueueFilters = {},
  options: UseAnalysisQueueOptions = {},
) {
  return useQuery({
    queryKey: queryKeys.analystQueue.list(filters as Record<string, unknown>),
    enabled: options.enabled ?? true,
    queryFn: async (): Promise<UseAnalysisQueueResult> => {
      const response = await apiClient.get<ApiSuccess<AnalysisQueueBackendResponse>>(
        '/admin/proposals/analysis-queue',
        { params: filters },
      );
      return response.data.data;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useAnalysisQueueInfinite(
  filters: AnalysisQueueInfiniteFilters,
  options: UseAnalysisQueueOptions = {},
) {
  return useInfiniteQuery({
    queryKey: queryKeys.analystQueue.kanbanColumn(filters as Record<string, unknown>),
    enabled: options.enabled ?? true,
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<AnalysisQueueInfinitePage> => {
      const params: AnalysisQueueFilters = {
        ...filters,
        view: 'kanban',
        page: pageParam,
        limit: ANALYSIS_KANBAN_PAGE_SIZE,
      };
      const response = await apiClient.get<ApiSuccess<AnalysisQueueBackendResponse>>(
        '/admin/proposals/analysis-queue',
        { params },
      );
      return response.data.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, page) => sum + page.rows.length, 0);
      if (fetched >= lastPage.count) return undefined;
      if (lastPage.rows.length < ANALYSIS_KANBAN_PAGE_SIZE) return undefined;
      return allPages.length + 1;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
